/**
 * Servicios cifrados con el esquema RSA híbrido (RSA PKCS#1 v1.5 + AES-256-CBC), el
 * mismo de CUSS. Para agregar/quitar un servicio cifrado de un dominio existente, edita
 * SOLO esta lista. Un dominio que cifre el request requiere además la pública de su BFF
 * en `environment.publics` (`<dominio>BffP`) y en `BFF_P` (rsa-key.service).
 *
 * Identifica, por (método HTTP + ruta normalizada), las operaciones cuyo request y/o
 * response viajan cifrados. ApiEncryptionInterceptor aplica el cifrado; ApiInterceptor
 * lo consulta para no enviar datos en claro a New Relic (salvo los servicios con
 * `newRelicRedaction: false`).
 *
 * Modos de request (todos mandan `x-client-public-key`):
 *  - `none`:  el request viaja en claro; solo se descifra la respuesta.
 *  - `query`: los params se serializan a JSON y se envían como `?data=<sobre>`.
 *  - `body`:  el body se serializa a JSON y se envía como el sobre (`application/json`).
 * El sobre es `{key, iv, data}`: `data` cifrado con una llave AES de un solo uso y
 * `key` (esa llave) cifrada con la pública del BFF.
 *
 * La respuesta 2xx de todos los servicios registrados llega como el mismo sobre, con
 * `key` cifrada con la pública de la sesión del kiosko; los errores 4xx/5xx llegan en
 * claro. Excepción: los marcados `encrypted: false` ya van por el BFF pero viajan en
 * claro (el BFF aún no los cifra).
 */

import { normalizePath } from '@app/utils/functions/normalize-path';

/** Dominio de negocio; define con qué pública del BFF (`BFF_P`) se cifra el request. */
export type EncryptionDomain = 'myTrips' | 'checkin' | 'payment' | 'ancillaries' | 'exchanges';

export type EncryptedRequestMode = 'none' | 'query' | 'body';

export interface EncryptedService {
  /** Identificador legible (logs y specs). */
  id: string;
  domain: EncryptionDomain;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  /** Regex contra la ruta normalizada (sin host, query ni slash final). */
  path: RegExp;
  request: EncryptedRequestMode;
  /**
   * `false`: las métricas de New Relic se envían en claro, como antes del cifrado
   * (docheckin, getBoardingPass, bagtag, flightDetails, flightstatus, profile,
   * updateDocumentsSecurity y getPassengerData, por decisión de negocio; aplica a todo
   * servicio nuevo del BFF). Por defecto se redactan.
   */
  newRelicRedaction?: boolean;
  /**
   * `false`: el servicio ya va por el BFF pero el BFF aún no cifra; request y respuesta
   * viajan en claro y `findEncryptedService` lo ignora. Cuando el BFF cifre, basta con
   * quitar esta marca. Por defecto `true`.
   */
  encrypted?: boolean;
}

// Las regex no se anclan al inicio: la URL base puede incluir una ruta (p. ej. .../host/api).
export const ENCRYPTED_SERVICES: readonly EncryptedService[] = [
  // MyTrips (la versión sale de environment.versionMyTrips)
  {
    id: 'pnrLookup',
    domain: 'myTrips',
    method: 'GET',
    path: /\/v\d+\/kiosk\/mytrips\/reservation$/,
    request: 'none',
  },
  // Check-in (la versión sale de environment.versionCheckin)
  {
    id: 'passengerListUpgrade',
    domain: 'checkin',
    method: 'GET',
    path: /\/v\d+\/kiosk\/checkin\/passengerListUpgrade\/v\d+$/,
    request: 'query',
  },
  {
    id: 'passengerListStandby',
    domain: 'checkin',
    method: 'GET',
    path: /\/v\d+\/kiosk\/checkin\/passengerlist\/v\d+\/standby$/,
    request: 'query',
  },
  {
    id: 'getViewDocuments',
    domain: 'checkin',
    method: 'POST',
    path: /\/v\d+\/kiosk\/checkin\/checkInDocuments\/getViewDocuments$/,
    request: 'body',
  },
  {
    id: 'eligibility',
    domain: 'checkin',
    method: 'POST',
    path: /\/v\d+\/kiosk\/checkin\/v\d+\/eligibility$/,
    request: 'body',
  },
  {
    id: 'updateDocuments',
    domain: 'checkin',
    method: 'POST',
    path: /\/v\d+\/kiosk\/checkin\/v\d+\/updateDocuments$/,
    request: 'body',
  },
  {
    id: 'docheckin',
    domain: 'checkin',
    method: 'POST',
    path: /\/v\d+\/kiosk\/checkin\/docheckin$/,
    request: 'body',
    newRelicRedaction: false,
  },
  {
    id: 'getBoardingPass',
    domain: 'checkin',
    method: 'POST',
    path: /\/v\d+\/kiosk\/checkin\/getBoardingPass$/,
    request: 'body',
    newRelicRedaction: false,
  },
  {
    id: 'bagtagEligibility',
    domain: 'checkin',
    method: 'POST',
    path: /\/v\d+\/kiosk\/checkin\/bagtag\/eligibility$/,
    request: 'body',
    newRelicRedaction: false,
  },
  {
    id: 'bagtag',
    domain: 'checkin',
    method: 'POST',
    path: /\/v\d+\/kiosk\/checkin\/bagtag$/,
    request: 'body',
    newRelicRedaction: false,
  },
  {
    id: 'flightDetails',
    encrypted: false,
    domain: 'checkin',
    method: 'POST',
    path: /\/v\d+\/kiosk\/checkin\/flightDetails$/,
    request: 'body',
    newRelicRedaction: false,
  },
  {
    id: 'flightStatus',
    encrypted: false,
    domain: 'checkin',
    method: 'GET',
    path: /\/v\d+\/kiosk\/checkin\/flightstatus$/,
    request: 'query',
    newRelicRedaction: false,
  },
  {
    id: 'kioskProfile',
    encrypted: false,
    domain: 'checkin',
    method: 'GET',
    path: /\/v\d+\/kiosk\/checkin\/profile$/,
    request: 'query',
    newRelicRedaction: false,
  },
  {
    id: 'updateDocumentsSecurity',
    domain: 'checkin',
    method: 'POST',
    path: /\/v\d+\/kiosk\/checkin\/updateDocumentsSecurity$/,
    request: 'body',
    newRelicRedaction: false,
  },
  {
    id: 'getPassengerData',
    domain: 'checkin',
    method: 'POST',
    path: /\/v\d+\/kiosk\/checkin\/getPassengerData$/,
    request: 'body',
    newRelicRedaction: false,
  },
  // Pagos (la versión sale de environment.versionPaymentsBFF). Ya van por el BFF de pagos;
  // payment-revenue-dc-exchange va cifrado con `paymentBffP` (en `publics`), el resto aún
  // sin cifrar (encrypted: false).
  {
    id: 'emitTicketAncillary',
    encrypted: false,
    domain: 'payment',
    method: 'POST',
    path: /\/v\d+\/kiosk\/payments\/emit-ticket\/ancillary$/,
    request: 'body',
    newRelicRedaction: false,
  },
  {
    id: 'pectabReceipt',
    encrypted: false,
    domain: 'payment',
    method: 'POST',
    path: /\/v\d+\/kiosk\/payments\/pectab-receipt\/generate$/,
    request: 'body',
    newRelicRedaction: false,
  },
  {
    id: 'paymentRevenueDcExchange',
    domain: 'payment',
    method: 'POST',
    path: /\/v\d+\/kiosk\/payments\/payment-revenue-dc-exchange$/,
    request: 'body',
    newRelicRedaction: false,
  },
  // Ancillaries (la versión sale de environment.versionAncillariesBFF). Ya van por el BFF de
  // ancillaries, pero aún sin cifrar (encrypted: false); al cifrar requerirán
  // `ancillariesBffP` en `publics`. chubb/bind sigue en el micro (/ac/chubb/bind) mientras
  // ancillaries-chubb-bind del BFF sea provisional; PUT /ac/ancillaries/v2/{pnr}, reassign/seat
  // y exchange/reassociation (IROPS) siguen en el micro.
  {
    id: 'ancillaries',
    encrypted: false,
    domain: 'ancillaries',
    method: 'POST',
    path: /\/v\d+\/kiosk\/ancillaries\/ancillaries-offer-baggage$/,
    request: 'body',
    newRelicRedaction: false,
  },
  {
    id: 'seatmap',
    encrypted: false,
    domain: 'ancillaries',
    method: 'POST',
    path: /\/v\d+\/kiosk\/ancillaries\/seatmap-offer-seats$/,
    request: 'body',
    newRelicRedaction: false,
  },
  {
    id: 'seat',
    encrypted: false,
    domain: 'ancillaries',
    method: 'POST',
    path: /\/v\d+\/kiosk\/ancillaries\/seat-seating$/,
    request: 'body',
    newRelicRedaction: false,
  },
  {
    id: 'upgradeOffers',
    encrypted: false,
    domain: 'ancillaries',
    method: 'POST',
    path: /\/v\d+\/kiosk\/ancillaries\/ancillaries-offer-upgrade$/,
    request: 'body',
    newRelicRedaction: false,
  },
  {
    id: 'chubbOffers',
    encrypted: false,
    domain: 'ancillaries',
    method: 'POST',
    path: /\/v\d+\/kiosk\/ancillaries\/ancillaries-offer-chubb$/,
    request: 'body',
    newRelicRedaction: false,
  },
  // Exchanges / IROPS (la versión sale de environment.versionExchangesBFF). Ya van por el BFF,
  // pero aún sin cifrar (encrypted: false). El resto de /sc/ sigue en el micro.
  {
    id: 'exchangesEligibility',
    encrypted: false,
    domain: 'exchanges',
    method: 'POST',
    path: /\/v\d+\/kiosk\/exchanges\/eligibilities$/,
    request: 'body',
    newRelicRedaction: false,
  },
  {
    id: 'exchangesReservations',
    encrypted: false,
    domain: 'exchanges',
    method: 'GET',
    path: /\/v\d+\/kiosk\/exchanges\/reservations$/,
    request: 'query',
    newRelicRedaction: false,
  },
];

/** Headers con datos del pasajero que no deben reportarse en claro para servicios cifrados. */
export const ENCRYPTED_SERVICE_SENSITIVE_HEADERS = ['pnr', 'ticket', 'lastName', 'firstName'];

/** Valor que sustituye los datos redactados en métricas. */
export const ENCRYPTED_REDACTION = '[ENCRYPTED]';

/**
 * Devuelve la entrada de la configuración que corresponde a la operación, o
 * `undefined` si el servicio no está cifrado (no registrado o con `encrypted: false`).
 *
 * @param method método HTTP del request.
 * @param url URL del request (absoluta o relativa).
 */
export function findEncryptedService(method: string, url: string): EncryptedService | undefined {
  const httpMethod = (method || '').toUpperCase();
  const path = normalizePath(url);
  return ENCRYPTED_SERVICES.find(
    (service) =>
      service.encrypted !== false && service.method === httpMethod && service.path.test(path)
  );
}
